import React from 'react';
import './Terms.css';

export const TERMS_VERSION = '2.1';

interface Props {
  onBack?: () => void;
}

export default function Terms({ onBack }: Props) {
  return (
    <div className="terms-root">
      <div className="terms-card">
        <div className="terms-header">
          <TermsShieldIcon />
          <div>
            <h1 className="terms-title">Términos y Condiciones de Uso</h1>
            <p className="terms-subtitle">StorSafe · Sistema de Gestión de Bodegas Químicas · Versión 2.1</p>
          </div>
        </div>

        <div className="terms-body">

          <section className="terms-section">
            <h2>1. Descripción del Servicio</h2>
            <p>
              StorSafe es una plataforma de software como servicio (SaaS) diseñada para la gestión profesional
              de bodegas de almacenamiento de productos químicos. El sistema proporciona herramientas basadas
              en normativa ecuatoriana e internacional, incluyendo: inventario de productos químicos con
              clasificación IMDG/ONU, matrices de compatibilidad química según la norma INEN 2266, gestión de
              hojas de seguridad (SDS), etiquetado según el Sistema Globalmente Armonizado (SGA/GHS),
              generación automática de planes de emergencia basados en la Guía ERG 2024, reportes de
              cumplimiento normativo, y gestión de bodegas y usuarios con roles diferenciados.
            </p>
            <p>
              El acceso al servicio se realiza mediante suscripción a uno de los planes disponibles (Starter,
              Professional o Enterprise), cada uno con las capacidades descritas en la plataforma al momento
              de la contratación.
            </p>
          </section>

          <section className="terms-section">
            <h2>2. Naturaleza de la Información — Herramienta de Apoyo</h2>
            <div className="terms-disclaimer-box">
              <strong>AVISO IMPORTANTE:</strong> StorSafe es una herramienta de apoyo y referencia. Toda la
              información generada o presentada por la plataforma —incluyendo, sin limitarse a, tablas de
              compatibilidad química, planes de emergencia, clasificaciones SGA, reportes de cumplimiento y
              recomendaciones de cualquier tipo— es de carácter orientativo y constituye una asistencia
              documental, no una asesoría técnica, legal ni profesional certificada.
            </div>
            <p>
              La responsabilidad exclusiva sobre el manejo, almacenamiento, clasificación y gestión de productos
              químicos recae en el usuario y la organización que contrata el servicio. StorSafe no reemplaza
              la consulta con ingenieros especializados en seguridad química, bomberos, autoridades
              regulatorias u otros profesionales competentes.
            </p>
          </section>

          <section className="terms-section">
            <h2>3. Limitación de Responsabilidad</h2>
            <p>
              En la máxima medida permitida por la legislación ecuatoriana aplicable, StorSafe y sus propietarios,
              directores, empleados y colaboradores no asumen responsabilidad por:
            </p>
            <ul>
              <li>Daños directos, indirectos, incidentales, especiales o consecuentes derivados del uso o la
                imposibilidad de uso de la plataforma.</li>
              <li>Lucro cesante, pérdida de datos, interrupción de operaciones o cualquier otra pérdida de
                carácter comercial o económico.</li>
              <li>Accidentes, incidentes, lesiones, enfermedades ocupacionales o daños a personas, bienes o
                medio ambiente que resulten de decisiones tomadas con base en la información, documentos o
                evaluaciones generadas por StorSafe.</li>
              <li>Inexactitudes, omisiones o desactualizaciones en las tablas de compatibilidad química,
                catálogos normativos (CIIU, frases de peligro SGA, checklists de inspección) u otros datos de
                referencia incluidos en la plataforma.</li>
              <li>Interrupciones del servicio por mantenimiento, fallas técnicas, fallas de proveedores externos
                (incluyendo pasarelas de pago) o causas de fuerza mayor.</li>
            </ul>
            <p>
              El usuario acepta que el uso de StorSafe es bajo su propio riesgo y criterio profesional.
            </p>
          </section>

          <section className="terms-section">
            <h2>4. Obligaciones del Usuario</h2>
            <p>Al utilizar StorSafe, el usuario se compromete a:</p>
            <ul>
              <li>Proporcionar información veraz y actualizada sobre los productos, bodegas y demás datos
                registrados en la plataforma.</li>
              <li>Verificar de forma independiente la información de compatibilidad, seguridad y cumplimiento
                normativo con fuentes oficiales y profesionales competentes antes de tomar decisiones
                operativas.</li>
              <li>Mantener las credenciales de acceso de forma segura y notificar de inmediato cualquier
                uso no autorizado de su cuenta.</li>
              <li>No compartir el acceso a la plataforma con personas externas a la organización contratante
                más allá del número de usuarios autorizado por el plan contratado.</li>
              <li>Cumplir con todas las normativas locales, nacionales e internacionales aplicables al
                almacenamiento y manejo de sustancias peligrosas y a la seguridad y salud ocupacional.</li>
              <li>No utilizar la plataforma para actividades ilegales o que contravengan las normativas
                de seguridad química o de SSO vigentes.</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>5. Propiedad Intelectual</h2>
            <p>
              Todo el software, diseño, código fuente, algoritmos, bases de datos, textos, gráficos y demás
              elementos que conforman StorSafe son propiedad exclusiva de sus creadores y están protegidos
              por las leyes de propiedad intelectual aplicables en Ecuador y tratados internacionales.
            </p>
            <p>
              La suscripción al servicio otorga al usuario una licencia de uso limitada, no exclusiva,
              intransferible y revocable para acceder a las funcionalidades contratadas. Queda prohibida
              la reproducción, distribución, modificación, ingeniería inversa o cualquier explotación
              comercial de los elementos de la plataforma sin autorización expresa por escrito.
            </p>
          </section>

          <section className="terms-section">
            <h2>6. Privacidad y Datos Personales</h2>
            <p>
              StorSafe almacena los datos ingresados por el usuario (inventario de productos, información
              de bodegas, datos de trabajadores, usuarios de la organización, y demás información operativa)
              en servidores seguros provistos por Supabase Inc., con medidas de seguridad estándar de la
              industria. Los datos son de propiedad exclusiva del usuario y no serán compartidos con terceros
              salvo requerimiento legal expreso o lo dispuesto en estos Términos.
            </p>
            <p>
              El usuario es responsable de no ingresar información que incluya datos personales de terceros
              sin contar con la base legal correspondiente, conforme a la Ley Orgánica de Protección de Datos
              Personales del Ecuador (LOPDP) y su Reglamento General.
            </p>
          </section>

          <section className="terms-section">
            <h2>7. Uso de Datos para Mejora del Servicio</h2>
            <p>
              StorSafe podrá utilizar los datos ingresados en la plataforma de forma anonimizada y agregada
              para mejorar el servicio, desarrollar nuevas funcionalidades, realizar análisis estadísticos y
              generar inteligencia sobre gestión de riesgos químicos y de seguridad ocupacional. En ningún caso
              se compartirán datos que permitan identificar a empresas, trabajadores o usuarios específicos
              sin su consentimiento expreso.
            </p>
          </section>

          <section className="terms-section">
            <h2>8. Integración con Sistemas de Terceros</h2>
            <p>
              StorSafe podrá integrar o compartir datos con sistemas de gestión de riesgos, seguridad
              industrial, respuesta a emergencias, pasarelas de pago u otras plataformas complementarias
              necesarias para la prestación del servicio. En el caso específico de procesamiento de pagos,
              StorSafe utiliza los servicios de proveedores de pago autorizados (actualmente PagoPlux/Plux S.A.)
              para procesar transacciones de suscripción; StorSafe no almacena directamente los datos completos
              de tarjetas de crédito o débito de los usuarios, los cuales son procesados directamente por el
              proveedor de pagos conforme a sus propios estándares de seguridad (PCI-DSS). Dichas integraciones
              se realizarán siempre preservando la confidencialidad de la información identificable que no sea
              estrictamente necesaria para la finalidad del tratamiento.
            </p>
          </section>

          <section className="terms-section">
            <h2>9. Definición de Datos Anonimizados</h2>
            <p>
              Se entiende por datos anonimizados aquellos que han sido procesados de tal manera que no pueden
              asociarse a una empresa, persona o instalación específica. StorSafe garantiza que cualquier uso
              externo de datos cumplirá con este estándar de anonimización y con lo dispuesto en la Ley
              Orgánica de Protección de Datos Personales del Ecuador.
            </p>
          </section>

          <section className="terms-section">
            <h2>10. Autorización de Acceso para Servicios de Emergencia y Control</h2>
            <p>
              El usuario podrá autorizar de forma expresa y voluntaria que la información de sus bodegas,
              zonas y productos químicos registrados en StorSafe sea accesible para el Cuerpo de Bomberos
              y servicios de emergencia autorizados, con los siguientes fines:
            </p>
            <ul>
              <li>(a) respuesta a emergencias e incidentes químicos;</li>
              <li>(b) inspecciones y controles técnicos de seguridad in situ;</li>
              <li>(c) verificación de cumplimiento normativo.</li>
            </ul>
            <p>
              Esta autorización es opcional, revocable en cualquier momento desde la configuración de la
              empresa, y aplica exclusivamente a entidades de seguridad pública debidamente acreditadas que
              operen dentro del ecosistema StorSafe. Sin esta autorización expresa, los datos de la empresa
              no serán compartidos con ningún tercero.
            </p>
          </section>

          <section className="terms-section">
            <h2>11. Suscripciones, Pagos y Renovación</h2>
            <p>
              El acceso a los planes de pago de StorSafe (Starter, Professional, Enterprise) está sujeto a
              las siguientes condiciones:
            </p>
            <ul>
              <li>Los precios publicados en la plataforma no incluyen el Impuesto al Valor Agregado (IVA), el
                cual se añade conforme a la tasa vigente en Ecuador al momento del cobro.</li>
              <li>Los planes de facturación anual se cobran como pago único correspondiente a los doce (12)
                meses de servicio.</li>
              <li>Los planes de facturación mensual están sujetos a renovación automática mensual por el
                mismo monto, salvo cancelación expresa del usuario realizada con al menos cinco (5) días de
                anticipación al siguiente ciclo de cobro.</li>
              <li>El usuario podrá cancelar su suscripción en cualquier momento desde la plataforma o
                contactando a soporte; la cancelación surte efecto al final del periodo ya pagado, sin
                reembolsos proporcionales salvo que la ley aplicable disponga lo contrario.</li>
              <li>StorSafe no es responsable por fallos, demoras o rechazos en el procesamiento de pagos
                atribuibles al proveedor de la pasarela de pago o a la entidad financiera emisora de la
                tarjeta del usuario.</li>
              <li>Ante la falta de pago, StorSafe podrá suspender el acceso a las funcionalidades del plan
                contratado, sin perjuicio de la conservación de los datos del usuario durante el periodo de
                gracia que se indique en la plataforma.</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>12. Modificaciones del Servicio y los Términos</h2>
            <p>
              StorSafe se reserva el derecho de modificar, suspender o descontinuar cualquier aspecto del
              servicio con aviso previo razonable. Los presentes términos pueden ser actualizados
              periódicamente; los cambios sustanciales serán comunicados mediante la plataforma. El uso
              continuado del servicio tras la notificación de cambios implica la aceptación de los nuevos términos.
            </p>
          </section>

          <section className="terms-section">
            <h2>13. Jurisdicción y Ley Aplicable</h2>
            <p>
              El presente acuerdo se rige por las leyes de la República del Ecuador. Cualquier controversia
              derivada del uso de la plataforma se someterá a los tribunales competentes de la ciudad de
              Quito, Ecuador, renunciando las partes a cualquier otro fuero que pudiere corresponderles.
            </p>
          </section>

          <section className="terms-section terms-section-last">
            <h2>14. Contacto</h2>
            <p>
              Para consultas sobre estos términos, soporte técnico o reportes de seguridad, puede contactar
              al equipo de StorSafe a través de los canales disponibles en la plataforma.
            </p>
            <p className="terms-version">
              Última actualización: Junio 2026 · Versión 2.1
            </p>
          </section>

        </div>

        {onBack && (
          <div className="terms-footer">
            <button className="terms-back-btn" onClick={onBack}>
              ← Volver
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function TermsShieldIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L3 7v6c0 5.25 3.75 10.15 9 11.25C17.25 23.15 21 18.25 21 13V7L12 2z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}
